import { useEffect, useRef } from 'react';
import { useClients } from 'src/clients/hooks/useClients';
import { useFamilies } from 'src/clients/hooks/useFamilies';
import useTemplates from 'src/data-capture/hooks/templates/useTemplates';
import { SuitabilityFlowPrefill } from './prefill';
import { NewSuitabilityData, STEP_ORDER } from './types';

type Props = {
  prefill: SuitabilityFlowPrefill;
  setData: React.Dispatch<React.SetStateAction<NewSuitabilityData>>;
  setStepIndex: (index: number) => void;
  onApplied: () => void;
};

/**
 * Resolves an approved Siesta proposal's ids into the client or household and template the flow
 * holds. Rendered only when the flow was opened with a prefill, so an ordinary flow fetches nothing
 * extra. The flow blocks its steps until `onApplied`; it cannot use `data.isLoading` for that,
 * because the appendices step writes that field from its own document loading.
 */
export function SuitabilityFlowPrefiller({ prefill, setData, setStepIndex, onApplied }: Props) {
  const isFamily = prefill.familyId !== null;
  const { clients, isLoading: clientsLoading } = useClients({}, { enabled: !isFamily });
  const { families, isLoading: familiesLoading } = useFamilies();
  const { files: templates, isLoading: templatesLoading } = useTemplates({
    templateType: 'SUITABILITY_REPORT',
  });
  const applied = useRef(false);

  const ownerLoading = isFamily ? familiesLoading : clientsLoading;

  useEffect(() => {
    if (applied.current || ownerLoading || templatesLoading) return;
    applied.current = true;

    const template = templates.find(t => t.id === prefill.templateId);
    const client = isFamily ? undefined : clients.find(c => c.id === prefill.clientId);
    const family = isFamily ? families.find(f => f.id === prefill.familyId) : undefined;

    setData(prev => {
      const common = {
        ...prev,
        template,
        reportFormat: prefill.reportFormat,
        adviceFiles: prefill.adviceFiles,
        feeDocuments: prefill.feeDocuments,
      };
      return isFamily
        ? { ...common, isFamily: true, family }
        : { ...common, isFamily: false, client };
    });

    if (!(client ?? family)) {
      setStepIndex(STEP_ORDER.indexOf('client'));
    } else if (!template) {
      setStepIndex(STEP_ORDER.indexOf('template'));
    }
    onApplied();
  }, [
    ownerLoading,
    templatesLoading,
    templates,
    clients,
    families,
    isFamily,
    prefill,
    setData,
    setStepIndex,
    onApplied,
  ]);

  return null;
}
